import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtD = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

const HELP = {
  medical: "Jugadores con el reconocimiento médico caducado o a punto de caducar. Puedes actualizar la fecha aquí mismo.",
  signing: "Jugadores de la base de scouting marcados como «Fichar». Cambia su estado deportivo para resolver el aviso.",
  rffm: "Alertas del Motor RFFM pendientes de leer (altas/bajas de rivales, errores…).",
  attendance: "Equipos con entrenamientos pasados sin la asistencia registrada. Entra a registrarla.",
};
const TITLE = {
  medical: "Reconocimientos médicos",
  signing: "Alertas de fichaje",
  rffm: "Motor RFFM — alertas sin leer",
  attendance: "Asistencia sin registrar",
};
// Estados deportivos a los que se puede mover un jugador marcado como «Fichar».
const SIGNING_RESOLUTIONS = ["Seguimiento", "Citar a prueba", "Ver en directo", "Descartar", "Pendiente"];

export default function DashboardAlertDialog({ type, open, items, onItems, onClose, navigate }) {
  const [busy, setBusy] = useState(null);
  const [dates, setDates] = useState({});
  useEffect(() => { setDates({}); }, [open]);
  if (!open) return null;

  const list = items || [];

  const medLevel = (p) => (p.days_left < 0 ? "text-rose-700" : p.days_left <= 15 ? "text-orange-600" : "text-yellow-700");
  const medLabel = (p) =>
    p.days_left < 0 ? "CADUCADO" : p.days_left === 0 ? "caduca HOY" : `caduca en ${p.days_left} día${p.days_left !== 1 ? "s" : ""}`;

  const resolveMedical = async (it) => {
    const value = dates[it.id];
    if (!value) { toast.error("Indica la nueva fecha de caducidad"); return; }
    setBusy(it.id);
    try {
      await api.patch(`/players/${it.id}/quick-field`, { field: "medical_expiry", value });
      toast.success(`Reconocimiento actualizado: ${it.name}`);
      onItems(list.filter((x) => x.id !== it.id));
    } catch (e) { toast.error("No se pudo guardar"); }
    finally { setBusy(null); }
  };

  const resolveSigning = async (it, status) => {
    setBusy(it.id);
    try {
      await api.put(`/scouting-market/players/${it.id}`, { sporting_status: status });
      toast.success(`${it.player_name}: estado cambiado a «${status}»`);
      onItems(list.filter((x) => x.id !== it.id));
    } catch (e) { toast.error("No se pudo actualizar el estado"); }
    finally { setBusy(null); }
  };

  const markRffmRead = async () => {
    setBusy("__all__");
    try {
      await api.put("/rffm/alerts/read");
      toast.success("Todas las alertas marcadas como leídas");
      onItems([]);
    } catch (e) { toast.error("No se pudo marcar como leídas"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`dashboard-alert-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> {TITLE[type]}
            <span className="text-xs font-normal text-amber-700">({list.length})</span>
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">{HELP[type]}</p>

        {type === "rffm" && list.length > 0 && (
          <div className="flex justify-end -mt-1">
            <Button size="sm" variant="outline" className="h-7 text-xs border-purple-400 text-purple-700"
              disabled={busy === "__all__"} onClick={markRffmRead} data-testid="dashboard-alert-rffm-readall">
              {busy === "__all__" ? "…" : "Marcar todas como leídas"}
            </Button>
          </div>
        )}

        {list.length === 0 ? (
          <div className="py-6 text-center text-sm text-emerald-600 font-semibold" data-testid="dashboard-alert-empty">
            ✓ No queda ningún caso pendiente en esta alerta.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-200">
            {list.map((it, i) => (
              <div key={it.id || it.team_id || i} className="px-3 py-2" data-testid={`dashboard-alert-item-${type}-${i}`}>
                {type === "medical" && (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <button type="button" onClick={() => navigate(`/players/${it.id}/dashboard`)}
                        className="text-sm font-semibold text-slate-800 truncate hover:underline text-left flex-1">
                        {it.name} <span className="text-slate-400 font-normal">· {it.team_name}</span>
                      </button>
                      <span className={`text-[11px] font-bold whitespace-nowrap ${medLevel(it)}`}>
                        {medLabel(it)} · {fmtD(it.medical_expiry)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input type="date" value={dates[it.id] || ""}
                        onChange={(e) => setDates((d) => ({ ...d, [it.id]: e.target.value }))}
                        className="h-7 text-xs w-40" data-testid={`dashboard-alert-med-date-${i}`} />
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={busy === it.id} onClick={() => resolveMedical(it)}
                        data-testid={`dashboard-alert-med-save-${i}`}>
                        {busy === it.id ? "…" : "Guardar fecha"}
                      </Button>
                    </div>
                  </>
                )}

                {type === "signing" && (
                  <div className="flex items-center justify-between gap-3">
                    <button type="button" onClick={() => navigate("/scouting?tab=seguimiento&estado=Fichar")}
                      className="text-sm font-semibold text-slate-800 truncate hover:underline text-left flex-1">
                      {it.player_name}
                      <span className="text-slate-400 font-normal text-xs">
                        {it.category ? ` · ${it.category}` : ""}{it.current_team ? ` · ${it.current_team}` : ""}{it.score != null ? ` · ${it.score} pts` : ""}
                      </span>
                    </button>
                    <Select value="" onValueChange={(v) => resolveSigning(it, v)} disabled={busy === it.id}>
                      <SelectTrigger className="h-7 w-36 text-xs shrink-0" data-testid={`dashboard-alert-sign-status-${i}`}>
                        <SelectValue placeholder="Cambiar estado" />
                      </SelectTrigger>
                      <SelectContent>
                        {SIGNING_RESOLUTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {type === "rffm" && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-700">{it.message}</span>
                  </div>
                )}

                {type === "attendance" && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-slate-800 truncate flex-1">
                      {it.team_name}{it.category ? <span className="text-slate-400 font-normal"> · {it.category}</span> : null}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-bold text-amber-700 whitespace-nowrap">{it.count} sin registrar</span>
                      <Button size="sm" variant="outline" className="h-7 text-xs border-amber-400 text-amber-700"
                        onClick={() => { onClose(); navigate(`/trainings?team_id=${it.team_id}`); }}
                        data-testid={`dashboard-alert-att-goto-${i}`}>
                        Registrar <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Button>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
